import React, { useState, useCallback, useEffect } from 'react';
import { Input, Flex } from 'antd';
import { useSelector } from 'react-redux';
import TooltipButton from '../../../components/TooltipButton';
import AttachFileDropdown from './AttachFileDropdown';
import './MainChatAiWindow.css';
import { ArrowUpOutlined } from '@ant-design/icons';
import { StopChatIcon } from '../icons';
import { useTransformTemplateMutation } from '../../../features/template/templatesApi';
import AttachFileCard from './AttachFileCard';

const MultilineAiEditor = React.memo(
  React.forwardRef(
    (
      {
        onSubmit,
        isAnswering,
        contextOptions = [],
        onContextOpenSelect,
        selectedContext,
        showDropdownInput = false,
        dropdownSize = 'sm',
        allowNavigationOnSubmit = false,
        attachedFiles = [],
        setAttachedFiles = () => {},
        inputValue = '',
        setTemplateData,
        setTemplateTitle,
        setTemplateId,
        templateId,
        templateTitle,
        handleAbort,
      },
      ref
    ) => {
      const [showSendBelow, setShowSendBelow] = useState(false);
      const [containerWidth, setContainerWidth] = useState(0);
      const [value, setValue] = useState(inputValue || '');
      const [rows, setRows] = useState(1);
      const [localAttachedFiles, setLocalAttachedFiles] = useState([]);
      const isDisabled = isAnswering || !value.trim();

      const handleFileAttach = useCallback(
        (fileName, type) => {
          if (!fileName) return;
          let newFile = {};
          if (type === 'template') {
            newFile.id = fileName?.id || templateId;
            newFile.title = fileName?.title || templateTitle;
            newFile.url = '';
            newFile.isDirectlyAttached = true;
            newFile.isWorkspace = false;
          } else {
            const { title, name, id, url } = fileName;
            newFile.id = id;
            newFile.title = title || name;
            newFile.url = url;
            newFile.isDirectlyAttached = true;
            newFile.isWorkspace = type === 'workspace';
          }
          newFile.type = type;
          setLocalAttachedFiles((prev) => [...prev, newFile]);
        },
        [templateId, templateTitle, setLocalAttachedFiles]
      );

      const { activeFile } = useSelector((state) => state?.organization);
      const [triggerTransformTemplate, transformData] = useTransformTemplateMutation();

      const updateContainerWidth = useCallback(() => {
        const textarea = ref.current?.resizableTextArea?.textArea;
        if (!textarea) return;
        const styles = window.getComputedStyle(textarea);
        const paddingRight = parseInt(styles.paddingRight);
        setContainerWidth(textarea.clientWidth - paddingRight);
      }, [ref]);

      useEffect(() => {
        updateContainerWidth();
        const handleResize = () => updateContainerWidth();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }, [updateContainerWidth]);

      useEffect(() => {
        setValue(inputValue);
      }, [inputValue]);

      const checkTextOverflow = useCallback(() => {
        const textarea = ref.current?.resizableTextArea?.textArea;
        if (!textarea) return;

        const styles = window.getComputedStyle(textarea);
        const span = document.createElement('span');
        span.style.font = styles.font;
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';

        const lines = textarea.value.split('\n');
        const firstLineText = lines[0];
        span.textContent = firstLineText;

        document.body.appendChild(span);
        const textWidth = span.getBoundingClientRect().width;
        document.body.removeChild(span);

        setShowSendBelow(textWidth > containerWidth * 0.99 || lines.length > 1);
        setRows(Math.min(8, Math.max(1, lines.length)));
      }, [containerWidth, ref]);

      const handleKeyDown = useCallback(
        (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            if (templateId) {
              const data = {
                file_id: activeFile?.id,
                template_id: templateId,
              };
              triggerTransformTemplate(data);
              return;
            }
            e.preventDefault();
            if (isAnswering || !value.trim()) return;
            setValue('');
            setRows(1);
            setShowSendBelow(false);
            onSubmit?.(
              {
                question: value?.trim(),
                isQuestion: true,
                isAnswerable: true,
                allowGoBack: false,
                attachedFiles: localAttachedFiles,
              },
              { shouldNavigate: allowNavigationOnSubmit }
            );
            setLocalAttachedFiles([]);
          } else if (e.key === 'Enter' && e.shiftKey) {
            const cursorPosition = e.target.selectionStart;
            const textBeforeCursor = value.substring(0, cursorPosition);
            const textAfterCursor = value.substring(cursorPosition);

            setValue(textBeforeCursor + '\n' + textAfterCursor);
            setRows((prev) => prev + 1);
            checkTextOverflow();
          }
        },
        [
          value,
          isAnswering,
          onSubmit,
          allowNavigationOnSubmit,
          checkTextOverflow,
          setLocalAttachedFiles,
          templateId,
          activeFile,
          triggerTransformTemplate,
          localAttachedFiles,
        ]
      );

      const handleSubmit = useCallback(() => {
        if (templateId) {
          const data = {
            file_id: activeFile?.id,
            template_id: templateId,
          };
          triggerTransformTemplate(data);
          return;
        }
        if (value.trim() && !isAnswering) {
          onSubmit?.(
            {
              question: value.trim(),
              isQuestion: true,
              isAnswerable: true,
              allowGoBack: false,
              attachedFiles: localAttachedFiles,
            },
            { shouldNavigate: allowNavigationOnSubmit }
          );
          setValue('');
          setRows(1);
          setShowSendBelow(false);
          setLocalAttachedFiles([]);
        }
      }, [
        value,
        isAnswering,
        onSubmit,
        allowNavigationOnSubmit,
        setLocalAttachedFiles,
        templateId,
        activeFile,
        triggerTransformTemplate,
        localAttachedFiles,
      ]);

      const handleInput = (e) => {
        setValue(e.target.value);
        checkTextOverflow();
      };

      const handleFileRemove = (item, type) => {
        const updatedFiles = localAttachedFiles.filter((f) => f.id !== item.id);
        setLocalAttachedFiles(updatedFiles);
      };

      return (
        <div className="onedocai-editor">
          <div className="onedocai-editor-inner" style={{ borderRadius: showSendBelow ? '.5rem' : '1rem' }}>
            <Flex gap="middle" vertical={false} align="center" wrap>
              {localAttachedFiles?.map((file) => (
                <AttachFileCard
                  key={file.id}
                  filename={file.title}
                  filetype={file.type}
                  previewUrl={file?.url}
                  style={{ width: '160px' }}
                  showRemove={true}
                  onRemove={() => {
                    const updatedFiles = localAttachedFiles.filter((f) => f.id !== file.id);
                    setLocalAttachedFiles(updatedFiles);
                  }}
                />
              ))}
            </Flex>

            <div className="chat-input-wrapper with-attached-files">
              <Input.TextArea
                ref={ref}
                placeholder="Ask anything"
                autoSize={{ minRows: 1, maxRows: 8 }}
                value={value}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                className="chat-input"
                variant="borderless"
                disabled={isAnswering}
              />
            </div>

            <div className="onedocai-editor_layoutTop">
              <div className="attach-btn-wrapper">
                <AttachFileDropdown
                  transformData={transformData}
                  setTemplateId={(data) => setTemplateId(data)}
                  setTemplateTitle={(data) => setTemplateTitle(data)}
                  setTemplateData={(data) => setTemplateData(data)}
                  onAttach={handleFileAttach}
                  selectedFiles={localAttachedFiles}
                  onRemoveFile={handleFileRemove}
                />
              </div>

              {!showSendBelow && !isAnswering && (
                <div className="onedocai-editor_layoutTop-actions">
                  <TooltipButton
                    disabled={isDisabled}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#1677FF',
                      color: '#fff',
                    }}
                    type="primary"
                    tooltipText={'Send'}
                    icon={<ArrowUpOutlined />}
                    onClick={handleSubmit}
                  />
                </div>
              )}

              {isAnswering && (
                <TooltipButton
                  type="text"
                  onClick={handleAbort}
                  icon={<StopChatIcon />}
                  tooltipText={'Stop'}
                  style={{
                    padding: 0,
                    background: 'transparent',
                    width: '24px',
                    height: '24px',
                  }}
                />
              )}
            </div>

            {showSendBelow && !isAnswering && (
              <div className="onedocai-editor_layoutBottom">
                <div className="action-group right-action-group">
                  <TooltipButton
                    disabled={isDisabled}
                    style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                    type="primary"
                    tooltipText={'Send'}
                    icon={<ArrowUpOutlined />}
                    onClick={handleSubmit}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
  )
);

export default MultilineAiEditor;
